import capitalize from 'lodash-es/capitalize';

import { create, v, w } from '@dojo/framework/core/vdom';
import { theme } from '@dojo/widgets/middleware/theme';

// make generated .d.ts file valid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { WNodeFactory, WidgetProperties, DNode } from '@dojo/framework/core/interfaces';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { ThemeProperties } from '@dojo/framework/core/middleware/theme';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Chart } from 'chart.js';
import { numeral } from '@holmescorp/numeral';

import { createNlsMiddleware } from '@holmescorp/lcc-state';

import { ParentTree } from '@holmescorp/lcc-interfaces';
import { ChartProperties } from '@holmescorp/charts';
import { ScatterChart } from './scatter-chart';

import { scoreExam } from '../support/calc-cutscore';
import { improvePlugin } from '../support/improve-plugin';
import { strengthPlugin } from '../support/strength-plugin';

import css from '../css/cut-scores.m.css';

export interface CutScoresProperties {
	modules: ParentTree;
	label: string;
	instructions: string;
	quizPattern: string;
}

const modulesToRawscores = (modules: ParentTree, ditaroots: string[]): { [key: string]: number } => {
	const rv: { [key: string]: number } = {};
	let totalcorrect = 0;

	ditaroots.forEach((root) => {
		rv[root] = modules[root].data.pointscorrect;
		totalcorrect += rv[root];
	});
	rv.score = totalcorrect;

	return rv;
};

const defaultLabels = (ditaroots: string[]) => {
	const rv: { [key: string]: string | string[] } = {};

	ditaroots.forEach((root) => {
		rv[root] = capitalize(root);
	});

	return rv;
};

const nlsSettings = createNlsMiddleware('settings');
const nlsQuiz = createNlsMiddleware('quiz');
const factory = create({ theme, nlsSettings, nlsQuiz }).properties<CutScoresProperties>();

export const CutScores = factory(({ properties, middleware: { theme, nlsSettings, nlsQuiz } }) => {
	const { modules, quizPattern } = properties();
	console.log('ML cutscore modules: ', modules);
	const settingsNls = nlsSettings();
	const settingsMsgs = settingsNls.messages;
	const {
		format: formatSm,
		messages: { cutscores = '{}', cutscore_labels, cutscore_roots = '[]', cutscore_quizpattern = '' }
	} = settingsNls;
	const { format } = nlsQuiz();
	const cutscoreStats = JSON.parse(cutscores);
	const roots = JSON.parse(cutscore_roots);
	const cutscoreLabels = cutscore_labels ? JSON.parse(cutscore_labels) : defaultLabels(roots);
	console.log('ML cutscore roots: ', roots);
	console.log('ML cutscoreLabels: ', cutscoreLabels);
	const rawscores = modulesToRawscores(modules, roots);
	const scoring = scoreExam(
		cutscore_quizpattern ? cutscore_quizpattern : quizPattern,
		rawscores,
		roots,
		cutscoreStats
	);
	const data: { x: number; y: number }[] = [];
	const labels: string[] = [];
	const instructions = format && format('cutscore_instructions', { score: scoring.scaled });

	roots.forEach((root: string, idx: number) => {
		labels.push(`${idx}`);
		data.push({ y: idx, x: numeral(scoring[root]).format('0.000') });
	});

	const scales = {
		xAxes: [
			{
				type: 'linear',
				ticks: {
					beginAtZero: true,
					min: 0,
					max: 6,
					step: 1,
					callback: ((v: number) => {
						if (v === 3) {
							return '';
						}
						return null;
					}) as any
				},
				scaleLabel: { display: false },
				gridLines: {
					display: true,
					drawBorder: true,
					drawOnChartArea: true,
					drawTicks: false,
					borderDash: [8, 5],
					color: '#000'
				}
			}
		],
		yAxes: [
			{
				type: 'linear',
				ticks: {
					beginAtZero: true,
					min: 0,
					max: 5,
					callback: (v: number) => {
						return cutscoreLabels[roots[v]];
					}
				}
			}
		]
	};

	const chartLabel =
		formatSm && formatSm('cutscore_widget_label', { association: settingsMsgs[`path-${quizPattern}`] });
	const chartprops: ChartProperties = {
		ariaLabel: chartLabel,
		pluginCode: [improvePlugin, strengthPlugin],
		legend: { display: false, position: 'bottom' },
		options: {
			scales,
			tooltips: { enabled: false },
			elements: {
				point: {
					pointStyle: 'rectRot'
				}
			},
			layout: {
				padding: {
					left: 0,
					right: 16,
					top: 16,
					bottom: 16
				}
			},
			plugins: {
				colorschemes: {
					scheme: 'tableau.ColorBlind10'
				},
				improvearea: {
					label: {
						enabled: true,
						text: 'Areas to Improve',
						strokeColor: '#222222',
						fontSize: 14,
						fontStyle: 'bold'
					}
				},
				strengtharea: {
					label: {
						enabled: true,
						text: 'Areas of Strength',
						strokeColor: '#222222',
						fontSize: 14,
						fontStyle: 'bold'
					}
				}
			}
		},
		data: {
			datasets: [
				{
					label: 'scores',
					data,
					fill: false,
					backgroundColor: 'rgb(64, 103, 197)',
					borderColor: 'rgb(0, 83, 193)',
					borderWidth: 1,
					pointRadius: 10
				}
			]
		}
	};

	const themeCss = theme.classes(css);

	return v('div', {}, [
		v('div', { innerHTML: instructions }),
		v('div', { classes: [themeCss.root] }, [w(ScatterChart, chartprops)])
	]);
});
